/* Liora Notify Me wishlist header launcher
   Keeps Notify Me's existing desktop access and adds a matching header heart wherever access is missing, especially mobile.
   The custom heart delegates to Notify Me's own launcher so saved items remain app-managed.
*/
(() => {
  'use strict';

  const BUTTON_CLASS = 'liora-wishlist-header-button';
  const HEADER_SELECTOR = '#x-header-container, .section-header, header';
  const DESKTOP_QUERY = '(min-width: 1024px)';
  const ICON_COLLECTION_URL = '/collections/belle-tress-icon-collection';
  const ICON_COLLAGE_SELECTOR = '[id$="__collage_D4GGTK"] .x-block-image-1';
  let injectTimer = null;
  let observer = null;

  const classText = (el) => {
    if (!el) return '';
    if (typeof el.className === 'string') return el.className;
    return el.className && typeof el.className.baseVal === 'string' ? el.className.baseVal : '';
  };

  const descriptor = (el) => [
    el?.getAttribute?.('aria-label'),
    el?.getAttribute?.('title'),
    el?.getAttribute?.('href'),
    el?.getAttribute?.('data-testid'),
    el?.getAttribute?.('data-action'),
    el?.id,
    classText(el),
    el?.textContent
  ].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim().toLowerCase();

  const isWishlistDescriptor = (value) =>
    value.includes('wishlist') || value.includes('wish list') || value.includes('my saved');

  const normalizedControl = (el) => {
    if (!el) return null;
    if (el.matches?.('a, button, [role="button"]')) return el;
    return el.closest?.('a, button, [role="button"]') || null;
  };

  const isVisible = (el) => {
    if (!el || !el.isConnected) return false;
    const style = window.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    return style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0' &&
      rect.width > 0 &&
      rect.height > 0;
  };

  const linkIconCollectionImage = () => {
    document.querySelectorAll(ICON_COLLAGE_SELECTOR).forEach((block) => {
      if (block.dataset.lioraIconCollectionLink === 'true') return;

      block.dataset.lioraIconCollectionLink = 'true';
      block.setAttribute('role', 'link');
      block.setAttribute('tabindex', '0');
      block.setAttribute('aria-label', 'Shop the Belle Tress Icons Collection');
      block.style.cursor = 'pointer';

      const visitCollection = (event) => {
        if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;

        const interactive = event.target.closest?.('a, button, [role="button"]');
        if (interactive && interactive !== block) return;

        if (event.type === 'keydown') event.preventDefault();
        window.location.assign(ICON_COLLECTION_URL);
      };

      block.addEventListener('click', visitCollection);
      block.addEventListener('keydown', visitCollection);
    });
  };

  const candidateScore = (el) => {
    const value = descriptor(el);
    if (!isWishlistDescriptor(value) || el.classList?.contains(BUTTON_CLASS)) return -Infinity;

    const style = window.getComputedStyle(el);
    const inHeader = Boolean(el.closest?.(HEADER_SELECTOR));
    const inMain = Boolean(el.closest?.('main, [role="main"]'));
    let score = 0;

    if (value.includes('my wishlist')) score += 120;
    if (value.includes('wishlist drawer')) score += 90;
    if (value.includes('open wishlist')) score += 80;
    if (inHeader) score += 70;
    if (style.position === 'fixed' || style.position === 'sticky') score += 65;
    if (value.includes('access')) score += 35;
    if (value.includes('notify')) score += 25;
    if (value.includes('add to wishlist') || value.includes('remove from wishlist')) score -= 140;
    if (inMain && style.position !== 'fixed' && style.position !== 'sticky') score -= 80;
    if (isVisible(el)) score += 20;

    return score;
  };

  const wishlistCandidates = () => {
    const raw = Array.from(document.querySelectorAll(
      'a, button, [role="button"], [class*="wishlist" i], [id*="wishlist" i]'
    ));
    const unique = [];
    const seen = new Set();

    raw.forEach((el) => {
      const control = normalizedControl(el);
      if (!control || seen.has(control) || control.classList?.contains(BUTTON_CLASS)) return;
      seen.add(control);
      unique.push(control);
    });

    return unique
      .map((el) => ({ el, score: candidateScore(el) }))
      .filter((item) => Number.isFinite(item.score))
      .sort((a, b) => b.score - a.score);
  };

  const findWishlistLauncher = (visibleOnly = false) =>
    wishlistCandidates().find((item) => !visibleOnly || isVisible(item.el))?.el || null;

  const openWishlist = (attempt = 0) => {
    const launcher = findWishlistLauncher(false);
    if (launcher) {
      launcher.click();
      return;
    }

    if (attempt < 12) {
      window.setTimeout(() => openWishlist(attempt + 1), 250);
    }
  };

  const createButton = () => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = BUTTON_CLASS;
    button.setAttribute('aria-label', 'Open wishlist');
    button.setAttribute('title', 'Wishlist');
    button.style.cssText = [
      'display:inline-flex',
      'align-items:center',
      'justify-content:center',
      'width:40px',
      'height:40px',
      'min-width:40px',
      'padding:8px',
      'margin:0',
      'border:0',
      'background:transparent',
      'color:inherit',
      'cursor:pointer',
      'flex:0 0 auto',
      'line-height:1'
    ].join(';');

    button.innerHTML = `
      <svg aria-hidden="true" focusable="false" width="22" height="22"
        viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
        style="display:block;width:22px;height:22px;overflow:visible;fill:none !important">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"
          fill="none" style="fill:none !important" stroke="currentColor" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      openWishlist();
    });

    return button;
  };

  const findCartControls = (root) => {
    const selectors = [
      'a[href="/cart"]',
      'a[href$="/cart"]',
      'a[aria-label*="cart" i]',
      'button[aria-label*="cart" i]',
      '[aria-controls*="cart" i]',
      '[data-cart-icon]'
    ].join(',');

    const controls = [];
    const seen = new Set();

    root.querySelectorAll(selectors).forEach((el) => {
      const control = normalizedControl(el);
      if (!control || seen.has(control)) return;
      const value = descriptor(control);
      if (value.includes('add to cart') || value.includes('buy now')) return;
      seen.add(control);
      controls.push(control);
    });

    if (controls.length) return controls;

    root.querySelectorAll('a, button, [role="button"]').forEach((el) => {
      const value = descriptor(el);
      if (value.includes('cart') && !value.includes('add to cart') && !seen.has(el)) {
        seen.add(el);
        controls.push(el);
      }
    });

    return controls;
  };

  const findIconGroup = (control, root) => {
    const named = control.closest?.(
      '.x-container-header-icons, [class*="header-icons" i], [class*="header__icons" i]'
    );
    if (named && root.contains(named)) return named;

    let node = control.parentElement;
    let steps = 0;

    while (node && node !== root && steps < 6) {
      const clickable = new Set(
        Array.from(node.querySelectorAll('a, button, [role="button"]'))
          .map(normalizedControl)
          .filter(Boolean)
      );
      const rect = node.getBoundingClientRect();

      if (clickable.size >= 2 && clickable.size <= 8 && rect.height <= 120) {
        return node;
      }

      node = node.parentElement;
      steps += 1;
    }

    return control.parentElement;
  };

  const directChildWithin = (control, group) => {
    let child = control;
    while (child?.parentElement && child.parentElement !== group) {
      child = child.parentElement;
    }
    return child?.parentElement === group ? child : control;
  };

  const groupHasVisibleNativeWishlist = (group) =>
    Array.from(group.querySelectorAll('a, button, [role="button"], [class*="wishlist" i], [id*="wishlist" i]'))
      .some((el) => {
        const control = normalizedControl(el) || el;
        return !control.classList?.contains(BUTTON_CLASS) &&
          isWishlistDescriptor(descriptor(control)) &&
          isVisible(control);
      });

  const injectButtons = () => {
    linkIconCollectionImage();

    const roots = Array.from(document.querySelectorAll(HEADER_SELECTOR));
    const processedGroups = new Set();
    const desktop = window.matchMedia(DESKTOP_QUERY).matches;
    const visibleAppAccess = findWishlistLauncher(true);

    roots.forEach((root) => {
      findCartControls(root).forEach((cartControl) => {
        const group = findIconGroup(cartControl, root);
        if (!group || processedGroups.has(group) || !isVisible(group)) return;
        processedGroups.add(group);

        const existingCustom = group.querySelector(`.${BUTTON_CLASS}`);
        const nativeInGroup = groupHasVisibleNativeWishlist(group);
        const preserveExistingDesktopAccess = desktop && Boolean(visibleAppAccess);

        if (nativeInGroup || preserveExistingDesktopAccess) {
          existingCustom?.remove();
          return;
        }

        if (existingCustom) return;

        const button = createButton();
        const insertionPoint = directChildWithin(cartControl, group);
        group.insertBefore(button, insertionPoint);
      });
    });
  };

  const scheduleInject = () => {
    window.clearTimeout(injectTimer);
    injectTimer = window.setTimeout(injectButtons, 120);
  };

  const start = () => {
    injectButtons();
    [300, 1000, 2500].forEach((delay) => window.setTimeout(injectButtons, delay));

    document.addEventListener('shopify:section:load', scheduleInject);
    document.addEventListener('shopify:section:reorder', scheduleInject);
    window.addEventListener('resize', scheduleInject, { passive: true });
    window.addEventListener('orientationchange', scheduleInject);

    observer = new MutationObserver(scheduleInject);
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
